/**
 * PhysicianEditModal Component
 * 
 * Modal for creating or editing physician records
 * Saves changes to draft store instead of directly to API
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui'
import useDraftStore from '../stores/draftStore'
import { useAuthStore } from '../stores/authStore'

export default function PhysicianEditModal({
  isOpen,
  onClose,
  physician = null, // null for new physician
  onSave,
  isDraftEdit = false,
  // Draft into another store than the clinic's (the shared doctor directory).
  draftStore = useDraftStore,
  draftScope = null,
  // The change carries the whole record, form fields over the original. For
  // stores published by whole-document replacement, where a field left out
  // (licenses, compensation) would be erased.
  wholeRecord = false,
}) {
  const [searchParams] = useSearchParams()
  const { user, currentClinicId } = useAuthStore()
  const clinicId = draftScope || currentClinicId || searchParams.get('clinic') || 'tmates'
  
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    title: 'MD',
    npi: '',
    prefix: '',
    email: '',
    dob: '',
    accept_new_patients: true,
    accept_refill_patients: true,
    physician_dosespot_id: '',
    physician_config: {
      minimize_scripts: false,
      max_capacity: -1
    }
  })

  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  
  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (physician) {
        // Editing existing physician
        setFormData({
          first_name: physician.first_name || '',
          middle_name: physician.middle_name || '',
          last_name: physician.last_name || '',
          title: physician.title || 'MD',
          npi: physician.npi || '',
          prefix: physician.prefix || '',
          email: physician.email || '',
          dob: physician.dob || '',
          accept_new_patients: physician.accept_new_patients ?? true,
          accept_refill_patients: physician.accept_refill_patients ?? true,
          physician_dosespot_id: physician.physician_dosespot_id ?? '',
          physician_config: {
            minimize_scripts: physician.physician_config?.minimize_scripts || false,
            max_capacity: physician.physician_config?.max_capacity ?? -1
          }
        })
      } else {
        // New physician - reset to defaults
        setFormData({
          first_name: '',
          middle_name: '',
          last_name: '',
          title: 'MD',
          npi: '',
          prefix: '',
          email: '',
          dob: '',
          accept_new_patients: true,
          accept_refill_patients: true,
          physician_dosespot_id: '',
          physician_config: {
            minimize_scripts: false,
            max_capacity: -1
          }
        })
      }
      setHasChanges(false)
      setErrors({})
    }
  }, [isOpen, physician])
  
  // Initialize draft store when modal opens
  useEffect(() => {
    if (isOpen && user && clinicId) {
      const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = draftStore.getState()
      
      // Get user ID (backend uses 'id', not '_id')
      const userId = user.id
      
      // Re-initialize if store is not initialized OR if it was initialized with wrong userId/clinicId
      if (!isInitialized || storeUserId !== userId || storeClinicId !== clinicId) {
        console.log('[PHYSICIAN MODAL] Initializing draft store with:', { clinicId, userId })
        initialize(clinicId, userId)
      }
    }
  }, [isOpen, user, clinicId, draftStore])
  
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setHasChanges(true)
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }
  
  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      physician_config: {
        ...prev.physician_config,
        [field]: value
      }
    }))
    setHasChanges(true)
  }
  
  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required'
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required'
    if (!formData.npi.trim()) newErrors.npi = 'NPI is required'
    else if (!/^\d{10}$/.test(formData.npi)) newErrors.npi = 'NPI must be exactly 10 digits'
    if (!formData.prefix.trim()) newErrors.prefix = 'Prefix is required'
    else if (!/^[a-z0-9_-]+$/.test(formData.prefix.toLowerCase())) {
      newErrors.prefix = 'Prefix can only contain letters, numbers, hyphens, and underscores'
    }
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    // DOB is optional, but validate format if provided
    if (formData.dob.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(formData.dob)) {
      newErrors.dob = 'Date must be in YYYY-MM-DD format'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSave = async () => {
    if (!validateForm()) return
    
    setIsLoading(true)
    try {
      const saveData = { ...formData }
      if (saveData.physician_dosespot_id === '' || saveData.physician_dosespot_id === null) {
        delete saveData.physician_dosespot_id
      } else {
        // Keep physician_id in sync with the DoseSpot ID (physician_id is a string)
        saveData.physician_id = String(saveData.physician_dosespot_id)
      }

      if (isDraftEdit) {
        onSave?.(saveData)
        onClose()
        setHasChanges(false)
      } else {
        const { addChange } = draftStore.getState()

        const isNewPhysician = !physician || !physician._id
        const operation = isNewPhysician ? 'create' : 'update'

        addChange({
          resource_type: 'physician',
          resource_id: physician?._id || physician?.id || formData.prefix,
          operation: operation,
          original_data: physician || null,
          new_data: wholeRecord && physician ? { ...physician, ...saveData } : saveData,
          description: `${operation === 'create' ? 'Create' : 'Update'} physician: ${formData.first_name} ${formData.last_name}`
        })

        console.log(`[DRAFT] Saved ${operation} for physician:`, formData.first_name, formData.last_name)

        onSave?.(formData)
        onClose()
        setHasChanges(false)
      }
    } catch (error) {
      console.error('Failed to save physician to drafts:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleClose = () => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
        return
      }
    }
    onClose()
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {physician ? 'Edit Physician' : 'Add New Physician'}
          </DialogTitle>
          <DialogDescription>
            {physician 
              ? 'Make changes to the physician information. Changes will be saved as a draft.' 
              : 'Add a new physician to the clinic. Changes will be saved as a draft.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className={errors.first_name ? 'border-destructive' : ''}
                />
                {errors.first_name && (
                  <p className="text-sm text-destructive mt-1">{errors.first_name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className={errors.last_name ? 'border-destructive' : ''}
                />
                {errors.last_name && (
                  <p className="text-sm text-destructive mt-1">{errors.last_name}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="middle_name">Middle Name</Label>
                <Input
                  id="middle_name"
                  value={formData.middle_name}
                  onChange={(e) => handleChange('middle_name', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="title">Title *</Label>
                <Select value={formData.title} onValueChange={(value) => handleChange('title', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MD">MD</SelectItem>
                    <SelectItem value="DO">DO</SelectItem>
                    <SelectItem value="NP">NP</SelectItem>
                    <SelectItem value="PA">PA</SelectItem>
                    <SelectItem value="PharmD">PharmD</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {/* Identifiers */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Identifiers</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="npi">NPI * (10 digits)</Label>
                <Input
                  id="npi"
                  value={formData.npi}
                  onChange={(e) => handleChange('npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className={errors.npi ? 'border-destructive' : ''}
                  placeholder="1234567890"
                />
                {errors.npi && (
                  <p className="text-sm text-destructive mt-1">{errors.npi}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="prefix">Prefix * (lowercase, no spaces)</Label>
                <Input
                  id="prefix"
                  value={formData.prefix}
                  onChange={(e) => handleChange('prefix', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  className={errors.prefix ? 'border-destructive' : ''}
                  placeholder="dr-smith"
                  disabled={!!physician} // Don't allow changing prefix for existing physicians
                />
                {errors.prefix && (
                  <p className="text-sm text-destructive mt-1">{errors.prefix}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="physician_dosespot_id">DoseSpot ID</Label>
                <Input
                  id="physician_dosespot_id"
                  type="number"
                  value={formData.physician_dosespot_id}
                  onChange={(e) => handleChange('physician_dosespot_id', e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="12345"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value.toLowerCase())}
                  className={errors.email ? 'border-destructive' : ''}
                  placeholder="doctor@example.com"
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">{errors.email}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="dob">Date of Birth (YYYY-MM-DD)</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleChange('dob', e.target.value)}
                  className={errors.dob ? 'border-destructive' : ''}
                />
                {errors.dob && (
                  <p className="text-sm text-destructive mt-1">{errors.dob}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Patient Acceptance */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Patient Acceptance</h3>
            
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.accept_new_patients}
                  onChange={(e) => handleChange('accept_new_patients', e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Accept New Patients</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.accept_refill_patients}
                  onChange={(e) => handleChange('accept_refill_patients', e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Accept Refill Patients</span>
              </label>
            </div>
          </div>
          
          {/* Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Configuration</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.physician_config.minimize_scripts}
                  onChange={(e) => handleConfigChange('minimize_scripts', e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Minimize Scripts</span>
              </label>
              
              <div>
                <Label htmlFor="max_capacity">Max Capacity (-1 for unlimited)</Label>
                <Input
                  id="max_capacity"
                  type="number"
                  value={formData.physician_config.max_capacity}
                  onChange={(e) => handleConfigChange('max_capacity', parseInt(e.target.value) || -1)}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !hasChanges}>
            {isLoading ? 'Saving...' : 'Save to Drafts'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

